/**
 * Copyright Mántum All Rights Reserved.
 */
import Control from '../action';
import Message from '../../components/common/message';
import request from '@mantum/kanela-request';
import useTimeout from '../../hooks/useTimeout';
import { useState } from 'react';

import type { Response, HttpError, Kanela, DataSource } from '@mantum/kanela';

type PropTypes = {
    children?: React.ReactElement;
    dataSource?: DataSource;
    formRef?: React.RefObject<Kanela>;
    negative?: string;
    onNegative?: () => void;
    onSuccessSubmit?: (refresh?: string[]) => void;
    positive?: string;
    refresh?: string[];
};

export default function Content(props: PropTypes) {
    // prettier-ignore
    const {
        negative = "Cancel",
        positive = 'Accept'
    } = props

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<Response>({
        ok: true,
        title: ''
    });

    const timeout = message.title ? 5000 : null;

    const clearMessage = () =>
        setMessage({
            ok: true,
            title: ''
        });

    useTimeout(() => {
        clearMessage();
    }, timeout);

    const onPositive = () => {
        const values = props.formRef?.current?.get();
        if (!values) {
            clearMessage();
            return;
        }

        const url = props.dataSource?.url;
        const method = props.dataSource?.method;
        if (!url || !method) {
            return;
        }

        setLoading(true);
        const response = request.fetch(url, {
            method: method,
            body: values,
            headers: props.dataSource?.headers
        });

        response.then((response: Response) => {
            const message =
                response &&
                typeof response === 'object' &&
                'message' in response
                    ? `${response.message}`
                    : 'Registration has been successful';

            setLoading(false);
            setMessage({
                ok: true,
                title: message
            });

            if (props.onSuccessSubmit) {
                props.onSuccessSubmit(props.refresh);
            }
        });

        response.catch((errors: HttpError) => {
            setLoading(false);
            setMessage({
                ok: false,
                title: errors.message,
                errors: errors
            });
        });
    };

    return (
        <>
            {message && message.title && (
                <Message
                    messages={message.errors}
                    onClose={clearMessage}
                    status={message.ok ? 'Success' : 'Error'}
                    title={message.title}
                />
            )}
            {props.children}
            <Control
                loading={loading}
                negative={negative}
                onNegative={props.onNegative}
                onPositive={onPositive}
                positive={positive}
            />
        </>
    );
}
